/**
 * Particles background effect using tsParticles
 * Only loads on pages with #tsparticles element
 */

// Declare global tsParticles type (loaded dynamically)
declare global {
  interface Window {
    tsParticles: {
      load: (config: { id: string; options: ParticleOptions }) => Promise<void>;
    };
  }
}

interface ParticleOptions {
  autoPlay: boolean;
  backgroundMask: {
    composite: string;
    cover: { color: { value: string }; opacity: number };
    enable: boolean;
  };
  defaultThemes: object;
  delay: number;
  fullScreen: { enable: boolean; zIndex: number };
  detectRetina: boolean;
  duration: number;
  fpsLimit: number;
  particles: {
    number: { value: number; density: { enable: boolean; value_area: number } };
    color: { value: string[] };
    shape: {
      type: string;
      stroke: { width: number; color: string };
      polygon: { nb_sides: number };
      image: { src: string; width: number; height: number };
    };
    opacity: {
      value: number;
      random: boolean;
      anim: { enable: boolean; speed: number; opacity_min: number; sync: boolean };
    };
    size: {
      value: number;
      random: boolean;
      anim: { enable: boolean; speed: number; size_min: number; sync: boolean };
    };
    line_linked: {
      enable: boolean;
      distance: number;
      color: string;
      opacity: number;
      width: number;
    };
    move: {
      enable: boolean;
      speed: number;
      direction: string;
      random: boolean;
      straight: boolean;
      out_mode: string;
      bounce: boolean;
      attract: { enable: boolean; rotateX: number; rotateY: number };
    };
  };
  interactivity: {
    detect_on: string;
    events: {
      onhover: { enable: boolean; mode: string };
      onclick: { enable: boolean; mode: string };
      resize: boolean;
    };
    modes: {
      grab: { distance: number; line_linked: { opacity: number } };
      bubble: {
        distance: number;
        size: number;
        duration: number;
        opacity: number;
        speed: number;
      };
      repulse: { distance: number; duration: number };
      push: { particles_nb: number };
      remove: { particles_nb: number };
    };
  };
  retina_detect: boolean;
}

const particleOptions: ParticleOptions = {
  autoPlay: true,
  backgroundMask: {
    composite: "destination-out",
    cover: {
      color: { value: "#fff" },
      opacity: 1,
    },
    enable: false,
  },
  defaultThemes: {},
  delay: 0,
  fullScreen: {
    enable: false,
    zIndex: 0,
  },
  detectRetina: true,
  duration: 0,
  fpsLimit: 120,
  particles: {
    number: {
      value: 95,
      density: {
        enable: true,
        value_area: 6076.4368405685,
      },
    },
    color: {
      value: ["#F04A1D", "#F01D5E", "#67E5F4", "#ADFF2E", "#B958EC"],
    },
    shape: {
      type: "circle",
      stroke: {
        width: 0,
        color: "#000000",
      },
      polygon: {
        nb_sides: 6,
      },
      image: {
        src: "img/github.svg",
        width: 100,
        height: 100,
      },
    },
    opacity: {
      value: 1,
      random: true,
      anim: {
        enable: false,
        speed: 1,
        opacity_min: 0.1,
        sync: false,
      },
    },
    size: {
      value: 418,
      random: true,
      anim: {
        enable: false,
        speed: 55.14485514485514,
        size_min: 100,
        sync: false,
      },
    },
    line_linked: {
      enable: false,
      distance: 568.1863019752363,
      color: "#f04141",
      opacity: 1,
      width: 7.102328774690454,
    },
    move: {
      enable: true,
      speed: 5,
      direction: "none",
      random: false,
      straight: false,
      out_mode: "out",
      bounce: false,
      attract: {
        enable: false,
        rotateX: 4261.397264814273,
        rotateY: 2683.101981549727,
      },
    },
  },
  interactivity: {
    detect_on: "window",
    events: {
      onhover: {
        enable: false,
        mode: "repulse",
      },
      onclick: {
        enable: false,
        mode: "push",
      },
      resize: true,
    },
    modes: {
      grab: {
        distance: 203.7962037962038,
        line_linked: {
          opacity: 1,
        },
      },
      bubble: {
        distance: 599.4005994005994,
        size: 143.85614385614386,
        duration: 2,
        opacity: 0.3916083916083916,
        speed: 3,
      },
      repulse: {
        distance: 251.74393581341698,
        duration: 0.4,
      },
      push: {
        particles_nb: 4,
      },
      remove: {
        particles_nb: 2,
      },
    },
  },
  retina_detect: true,
};

/**
 * Dynamically load a script and return a promise
 */
function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    // Check if already loaded
    if (window.tsParticles) {
      resolve();
      return;
    }

    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
    document.head.appendChild(script);
  });
}

/**
 * Initialize particles background effect
 * Only loads the library if #tsparticles element exists
 */
export async function initParticles(): Promise<void> {
  const container = document.getElementById("tsparticles");
  if (!container) return;

  try {
    await loadScript("/vendor/tsparticles.slim.bundle.js");
    await window.tsParticles.load({
      id: "tsparticles",
      options: particleOptions,
    });
  } catch (error) {
    console.error("Failed to load tsParticles:", error);
  }
}
